import { useParams } from "react-router-dom";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { useTemplateV2 } from "../../../hooks/useTemplateV2";

import { Button } from "../../../components/ui/button";
import { EllipsisVerticalIcon, PlusIcon } from "lucide-react";
import { Popover, PopoverContent } from "../../../components/ui/popover";
import { PopoverTrigger } from "../../../components/ui/popover";
import Divider from "../../../components/templateManager/Divider";
import TemplateTable from "../../../components/templateManager/TemplateTable";
import { SingleValueGroups } from "../../../components/templateManager/SingleValueGroups";
import { TablePopover } from "../../../components/templateManager/TablePopover";
import SingleValuePopover from "../../../components/templateManager/SingleValuePopover";
import { UpdateTemplatePopover } from "../../../components/templateManager/UpdateTemplatePopover";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import { toast } from "sonner";

export const IndividualTemplateV2Page = () => {
  const { templateId } = useParams();
  const {
    template,
    jsonSchema,
    handleUpdateSchema,
    handleDeleteTable,
    handleRowKeyAction,
    handleColumnAction,
    handleTableAction,
    handleTableOrderAction,
    handleAttributeValueGroupAction,
    loading,
  } = useTemplateV2({
    templateId,
    fetchOnLoad: true,
  });

  return (
    <div className="h-screen flex flex-col w-full mx-auto bg-white overflow-y-auto">
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="px-4 mx-auto flex flex-col w-full max-w-4xl mt-24">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-semibold mb-4">
              {template?.name || "New Template"}
            </h1>
            {template && (
              <Popover>
                <PopoverTrigger asChild className="cursor-pointer">
                  <EllipsisVerticalIcon size={20} />
                </PopoverTrigger>
                <UpdateTemplatePopover
                  template={template}
                  updateTemplate={handleUpdateSchema}
                />
              </Popover>
            )}
          </div>
          <Divider />

          <div className="mt-8 mb-2">
            <h2 className="text-xl font-semibold">Tables</h2>
            <p className="text-font-secondary">
              Data you want to output in a table Eg. Cash Flow, Income
              Statements, Rent Roll etc.
            </p>
            {template?.tables.map((table) => (
              <TemplateTable
                key={table.id}
                table={table}
                handleColumnAction={handleColumnAction}
                handleTableAction={handleTableAction}
                handleDeleteTable={handleDeleteTable}
                handleTableOrderAction={handleTableOrderAction}
                handleRowKeyAction={handleRowKeyAction}
              />
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <Button className="mt-2 gap-1 text-gray-400" variant="ghost">
                  <PlusIcon /> New Table
                </Button>
              </PopoverTrigger>
              <TablePopover
                deleteTable={handleDeleteTable}
                updateTable={handleTableAction}
              />
            </Popover>
          </div>
          <Divider />
          <div className="my-8">
            <h2 className="text-xl font-semibold">Single Values</h2>
            <p className="text-font-secondary mb-4">
              Often a unique value found just once in your doc
            </p>
            {template?.valueGroups.map((valueGroup) => (
              <SingleValueGroups
                key={valueGroup.id}
                valueGroup={valueGroup}
                handleAttributeValueGroupAction={
                  handleAttributeValueGroupAction
                }
              />
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <Button className="mt-2 gap-1 text-gray-400" variant="ghost">
                  <PlusIcon /> New Value
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[500px]" align="start">
                <SingleValuePopover
                  onSave={handleAttributeValueGroupAction}
                  valueGroupId={template?.valueGroups[0]?.id}
                />
              </PopoverContent>
            </Popover>
          </div>

          <Accordion type="single" collapsible>
            <AccordionItem value="item-1">
              <AccordionTrigger>Developer Information</AccordionTrigger>
              <AccordionContent>
                <div>
                  <div>
                    Use this schema ID to extract data from your documents via
                    our API.{" "}
                    <a
                      className="underline text-blue-700"
                      href="https://midship.readme.io/reference/post_extract-async"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View API documentation
                    </a>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="font-medium">Schema ID:</span> {templateId}
                    <Button
                      className="ml-2"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(templateId || "");
                        toast.success("Schema ID copied to clipboard");
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  <div className="mt-2 font-medium">
                    JSON Return Type for API:
                  </div>
                  <div className="mt-1 overflow-auto rounded-md bg-gray-100 p-4 relative max-h-[90vh] ">
                    <pre>{JSON.stringify(jsonSchema, null, 2)}</pre>
                    <Button
                      className="absolute top-2 right-2 hover:bg-gray-200"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          JSON.stringify(jsonSchema, null, 2),
                        );
                        toast.success("JSON Return Type copied to clipboard");
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
};
